import React, { useEffect, useState, useContext, useCallback } from "react";
import api from "../../../API/axiosInstance";
import Typography from "@mui/material/Typography";
import { generateHash } from "../../../API/hashUtil";
import { MyContext } from "../../../MyContextApi";
import "../../../styles/ChartDataDrawer.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareFacebook,
  faYoutube,
  faInstagram,
  faSquareXTwitter,
  faSquareThreads,
  faReddit,
} from "@fortawesome/free-brands-svg-icons";
import {
  faPenToSquare,
  faClock,
  faUpRightFromSquare,
  faPen,
  faDownload,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import { CircularProgress } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import * as XLSX from "xlsx";

const ChartDatadrawer = ({
  language,
  sentiment,
  barsentiment,
  platform,
  postOwner,
  keyword,
  hashtag,
  section,
}) => {
  const { dashboardDate, setDashboardDate } = useContext(MyContext);
  const [response, setResponse] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [responseCount, setResponseCount] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false); // Global translation toggle state
  const token = localStorage.getItem("Token");
  const platformsParam = platform;
  const sectionsParam = section;
  const languagesParam = language;
  const sentimentsParam = sentiment?.toLowerCase();
  const intensityParam = "";
  const contentParam = "";
  const hashtagParam = "";
  let dashEnd;
  let dashStart;
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleScroll = useCallback((event) => {
    const target = event.target;
    const scrollTop = target.scrollTop;
    setShowScrollTop(scrollTop > 300);
  }, []);

  const scrollToTop = () => {
    const feedContainer = document.getElementById("scrollableDiv");
    if (feedContainer) {
      feedContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Toggle translation function
  const toggleTranslation = () => {
    setShowTranslation(!showTranslation);
  };

  useEffect(() => {
    const feedContainer = document.getElementById("scrollableDiv");
    if (feedContainer) {
      feedContainer.addEventListener("scroll", handleScroll);
      return () => feedContainer.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  if (dashboardDate && dashboardDate.length > 0) {
    dashStart = dashboardDate[0] && dashboardDate[0].startDate;
    dashEnd = dashboardDate[0] && dashboardDate[0].endDate;
  }

  const wordsentiment = sentiment?.toLowerCase();
  const stringParamsWord = {
    startDateStr: dashStart ? dashStart.toString() : "",
    endDateStr: dashEnd ? dashEnd.toString() : "",
    keyword: keyword ? keyword.toString() : "",
    postOwner: postOwner ? postOwner.toString() : "",
    hashTag: hashtag ? hashtag.toString() : "",
    language: language ? language.toString() : "",
    sentiment: wordsentiment ? wordsentiment.toString() : "",
    platform: platform ? platform.toString() : "",
    section: section ? section.toString() : "",
  };
  const hexHashWord = generateHash(stringParamsWord);

  const wordparams = {
    platform,
    section,
    language,
    sentiment: sentiment?.toLowerCase(),
    startDateStr: dashStart,
    endDateStr: dashEnd,
    keyword,
    postOwner,
    hashTag: hashtag,
    HPPcode: hexHashWord,
  };
  const smallbarsentiment = barsentiment?.toLowerCase();

  const stringParamsPlatform = {
    startDateStr: dashStart ? dashStart.toString() : "",
    endDateStr: dashEnd ? dashEnd.toString() : "",
    sentiment: smallbarsentiment ? smallbarsentiment.toString() : "",
    platform: platform ? platform.toString() : "",
  };
  const hexHashPlatform = generateHash(stringParamsPlatform);

  const platformparams = {
    platform,
    sentiment: barsentiment?.toLowerCase(),
    startDateStr: dashStart,
    endDateStr: dashEnd,
    HPPcode: hexHashPlatform,
  };

  const fetchData = async (pageNum, isPlatformData = false) => {
    if (isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const url = isPlatformData
        ? `/api/sentimentbyPlatform`
        : `/api/dashboardData`;

      const params = {
        ...(isPlatformData ? platformparams : wordparams),
        page: pageNum,
      };

      const response = await api.get(url, {
        params,
        headers: {
          "X-XSS-Protection": "1; mode=block",
          Authorization: `Bearer ${token}`,
          "X-Content-Type-Options": "nosniff",
          "X-Frame-Options": "DENY",
          "Referrer-Policy": "strict-origin-when-cross-origin",
          "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
          "Content-Type": "application/json",
        },
      });

      const { data, totalCount, totalPages } = response.data;

      if (pageNum === 1) {
        setResponse(data);
      } else {
        setResponse((prev) => [...prev, ...data]);
      }

      setResponseCount(totalCount);
      setTotalPages(totalPages);
      setHasMore(pageNum < totalPages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setResponse([]);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setResponse([]);
    setLoading(true);
    setHasMore(true);
    const isPlatformData = !!(platform || barsentiment);

    fetchData(1, isPlatformData);
  }, [
    language,
    sentiment,
    barsentiment,
    platform,
    section,
    postOwner,
    keyword,
    hashtag,
    dashboardDate,
  ]);

  const fetchMoreData = () => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    const isPlatformData = !!(platform || barsentiment);
    fetchData(nextPage, isPlatformData);
  };

  const endMessages = [
    "Oops! The data pantry is empty!",
    "That's all, folks! No more bytes to bite!",
    "The data train has reached its final stop!",
    "End of the scroll, nothing left to unroll!",
    "You've reached the end of the internet... almost!",
    "No more crumbs in the data cookie jar!",
    "The well of data has run dry!",
    "Sorry, the data elves are on a coffee break!",
    "All out of scrollable goodies!",
    "Data's all tucked in for the night!",
    "No more pixels to load. Time to relax!",
    "The data stream has gone on vacation!",
    "You've officially scrolled into the void!",
    "No more treasure in the data chest!",
    "The last data bubble has popped!",
    "You've cleaned out the data buffet!",
    "That's it, we've hit the digital wall!",
    "The data cupboard is bare!",
    "All data circuits are busy... forever!",
    "No more data sprouts in the garden!",
  ];

  // Keep all existing helper functions (renderPlatformIcon, capitalizeFirstLetter, etc.)
const renderPlatformIcon = (platform) => {
  const normalizedPlatform = String(platform || "")
    .trim()
    .replace(/\.com$/i, "")
    .replace(/[_\-\s]+/g, "")
    .toUpperCase();

  switch (normalizedPlatform) {
    case "FACEBOOK":
      return (
        <FontAwesomeIcon
          icon={faSquareFacebook}
          style={{
            fontSize: "20px",
            color: "#4267B2",
            backgroundColor: "#E9F1FB",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Facebook"
        />
      );

    case "TWITTER":
    case "X":
      return (
        <FontAwesomeIcon
          icon={faSquareXTwitter}
          style={{
            fontSize: "20px",
            color: "black",
            backgroundColor: "#E1F5FE",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Twitter"
        />
      );

    case "YOUTUBE":
      return (
        <div
          style={{
            width: "30px",
            height: "30px",
            backgroundColor: "#FFEBEB",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "5px",
          }}
          title="YouTube"
        >
          <FontAwesomeIcon
            icon={faYoutube}
            style={{
              fontSize: "20px",
              color: "#FF0000",
            }}
          />
        </div>
      );

    case "THREADS":
      return (
        <FontAwesomeIcon
          icon={faSquareThreads}
          style={{
            fontSize: "20px",
            color: "black",
            backgroundColor: "#E1F5FE",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Threads"
        />
      );

    case "REDDIT":
      return (
        <FontAwesomeIcon
          icon={faReddit}
          style={{
            fontSize: "20px",
            color: "#ff4500",
            backgroundColor: "#E1F5FE",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Reddit"
        />
      );

    default:
      console.warn("Unknown platform from API:", platform);

      return (
        <span
          style={{
            margin: "0 5px",
            color: "rgba(0, 0, 0, 0.7)",
            fontStyle: "italic",
          }}
        >
          {platform || "Unknown"}
        </span>
      );
  }
};
  function capitalizeFirstLetter(str) {
    if (typeof str === "string" && str.length > 0) {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }
    return str;
  }

  const paramMap = [
    { param: capitalizeFirstLetter(platformsParam), label: "Platform" },
    { param: sectionsParam, label: "Section" },
    { param: capitalizeFirstLetter(languagesParam), label: "Language" },
    { param: capitalizeFirstLetter(sentimentsParam), label: "Sentiment" },
    { param: capitalizeFirstLetter(intensityParam), label: "Intensity" },
    { param: capitalizeFirstLetter(contentParam), label: "Content" },
    { param: capitalizeFirstLetter(hashtagParam), label: "Hashtag" },
    { param: keyword, label: "Keyword" },
    { param: postOwner, label: "User" },
    { param: hashtag, label: "" },
  ];

  const getTitleParam = () => {
    const firstValidParam = paramMap.find(({ param }) => param);

    if (!firstValidParam) return "No data";

    const { param, label } = firstValidParam;

    const formattedParam =
      label === "Section"
        ? param.replace(
            /\(([IVXLCDM]+)\)/g,
            (_, roman) => `(${roman.toLowerCase()})`
          )
        : param;

    return `Data for "${formattedParam}" ${label}`;
  };

  const exportToExcel = () => {
    const formattedData = response.map((item) => ({
      "Post Owner": item.postOwner || "Unknown",
      Sentiment: item.sentiment,
      Platform: item.platform,
      Keyword: item.keyword,
      "Title/Content": item.platform === "YOUTUBE" ? item.title : item.content,
      Date: new Date(item.datetime).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
      Time: new Date(item.datetime).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    }));

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Feed Data");

    XLSX.writeFile(wb, "feed_data.xlsx");
  };

  return (
    <>
      <div className="feed-header">
        <div className="feed-title-container">
          <h2 className="feed-title">{getTitleParam()}</h2>
        </div>
        <Tooltip
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 600 }}
          title=<h4>Download</h4>
          arrow
        >
          <div onClick={exportToExcel} className="download-btn">
            <FontAwesomeIcon
              icon={faDownload}
              style={{ fontSize: "20px", color: "#024CAA" }}
            />
          </div>
        </Tooltip>
      </div>
      <div className="feed-container" id="scrollableDiv">
        {!loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
      
              // marginBottom: "10px",
            }}
          >
            <h5 className="feed-subtitle">{responseCount} Results</h5>

            {/* Global Translation Toggle */}
            <span
              onClick={toggleTranslation}
              style={{
                fontSize: "12px",
                color: "#024CAA",
                cursor: "pointer",
                textDecoration: "underline",
                fontFamily: "'Host Grotesk', sans-serif",
                fontWeight: "500",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#0356d6";
                e.target.style.textDecorationThickness = "2px";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#024CAA";
                e.target.style.textDecorationThickness = "1px";
              }}
            >
              {showTranslation
                ? "SHOWING TRANSLATION"
                : "SHOWING ORIGINAL CONTENT"}
            </span>
          </div>
        )}

        {loading ? (
          <div
            className="three-body-problem"
            style={{
              height: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="three-body">
              <div className="three-body__dot"></div>
              <div className="three-body__dot"></div>
              <div className="three-body__dot"></div>
            </div>
          </div>
        ) : (
          <>
            <InfiniteScroll
              dataLength={response.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                isLoadingMore && (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <CircularProgress size={30} />
                  </div>
                )
              }
              endMessage={
                <p
                  style={{
                    fontSize: "15px",
                    fontWeight: 400,
                    fontFamily: "'Host Grotesk', sans-serif",
                    textAlign: "center",
                    padding: "20px",
                    color: "#657786",
                  }}
                >
                  {response.length > 0
                    ? endMessages[
                        Math.floor(Math.random() * endMessages.length)
                      ]
                    : "No data available."}
                </p>
              }
              scrollableTarget="scrollableDiv"
            >
              <div className="feed">
                {response.map((item, index) => (
                  <div className="feed-item" key={index}>
                    <div className="header">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                        }}
                      >
                        <Tooltip
                          followCursor
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 600 }}
                          title=<h4>View Original Post</h4>
                          arrow
                        >
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="item-link"
                            style={{ marginLeft: "10px", color: "#024CAA" }}
                          >
                            <FontAwesomeIcon
                              icon={faUpRightFromSquare}
                              style={{
                                color: "#657786",
                                marginBottom: "4px",
                                fontSize: "12px",
                              }}
                            />
                          </a>
                        </Tooltip>
                        <Typography
                          variant="h6"
                          className="post-owner"
                          style={{
                            width: "330px",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                            fontWeight: "bold",
                            paddingBottom: "5px",
                            fontFamily: "'Host Grotesk', sans-serif",
                          }}
                        >
                          {item.postOwner || "Unknown"}
                        </Typography>
                      </div>
                      <div className="platform-sentiment">
                        <span className={`sentiment-bubble ${item.sentiment}`}>
                          {item.sentiment}
                        </span>
                        <span className="platform">
                          {renderPlatformIcon(item.platform)}
                        </span>
                      </div>
                    </div>
                    <span className="content">
                      {item.platform === "YOUTUBE"
                        ? item.title
                        : showTranslation && item.translation
                        ? item.translation
                        : item.content}
                    </span>
                    <div className="feed-footer">
                      <span
                        className="feed-footer-child"
                        style={{ color: "#024CAA", fontWeight: "800px" }}
                      >
                        {new Intl.DateTimeFormat("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "2-digit",
                          timeZone: "UTC",
                        }).format(new Date(item.datetime))}
                        , 
                        {new Intl.DateTimeFormat("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone: "UTC",
                        }).format(new Date(item.datetime))}
                      </span>
                      <Tooltip
                        followCursor
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 600 }}
                        title=<h4>Keyword Used</h4>
                        arrow
                      >
                        <span className="keyword">
                          <FontAwesomeIcon icon={faPen} /> {item.keyword}
                        </span>
                      </Tooltip>
                    </div>

                    {/* Removed per-post translation toggle */}
                  </div>
                ))}
              </div>
            </InfiniteScroll>

            {showScrollTop && (
              <Tooltip
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
                title={<h4>Scroll to Top</h4>}
                arrow
                placement="left"
              >
                <button
                  onClick={scrollToTop}
                  className="scroll-to-top-button"
                  aria-label="Scroll to top"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
              </Tooltip>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ChartDatadrawer;
